import qs from 'query-string';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GLOBAL_PER_PAGE } from 'routes/constants';
import {
  CountResponseType,
  MutationType,
  SegmentType,
  SegmentsResponseType,
} from '../../../shared/types';
import { getRulePayload } from '../../../shared/utils';
import request from '../../../utils/request';
import i18n from '../../../i18n';

export const useGetSegments = ({
  page_size = GLOBAL_PER_PAGE,
  page_token = '',
  callback,
}: MutationType & {
  page_size?: number;
  page_token?: string;
} = {}) => {
  return useQuery(
    'segments',
    async () => {
      const data: SegmentsResponseType = await request(
        `/contacts/segments/?page_size=${page_size}&next_token=${page_token}`,
      );
      return data;
    },
    {
      onSuccess: (response: SegmentsResponseType) => {
        if (callback) callback(true, response);
      },
      onError: (err: any) => {
        if (callback) callback(false, err);
      },
    },
  );
};

export const useGetSegment = ({
  callback,
  segmentId,
  showDeleted = false,
}: MutationType & {
  segmentId: string;
  showDeleted?: boolean;
}) => {
  return useQuery(
    ['segment', segmentId],
    async () => {
      if (!segmentId) return undefined;
      const newURL = qs.stringifyUrl({
        url: `/contacts/segments/${segmentId}/`,
        query: {
          ...(showDeleted ? { showDeleted: true } : {}),
        },
      });
      const data: SegmentType = await request(newURL);
      return data;
    },
    {
      enabled: Boolean(segmentId),
      onSuccess: (response: SegmentType) => {
        if (callback) callback(true, response);
      },
      onError: (err: any) => {
        if (callback) callback(false, err);
      },
    },
  );
};

export const useSaveSegment = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (segment: SegmentType) => {
      const newSegment: SegmentType = { ...segment };
      newSegment.rule = getRulePayload(
        newSegment.rule || { match_type: 'ALL', condition_sets: [] },
      );
      const data: SegmentType = await request(
        segment.id ? `/contacts/segments/${segment.id}` : '/contacts/segments/',
        segment,
        segment.id ? 'PATCH' : 'POST',
      );
      return data;
    },
    {
      onSuccess: (response, segment: SegmentType) => {
        queryClient.setQueryData(['segment', `${response.id}`], response);
        if (segment.id) {
          queryClient.setQueryData('segments', (old: any) => {
            const newData = { ...(old || {}) };
            if (old && old.segment_rules && old.segment_rules.length > 0) {
              newData.segment_rules = old.segment_rules.map((o: SegmentType) =>
                o.id === response.id ? response : o,
              );
            }
            return newData;
          });
        } else {
          queryClient.invalidateQueries('segments-count');
          queryClient.invalidateQueries('segments');
        }
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: segment.id
              ? `${i18n.t('forms.toast.success.segment_updated')}`
              : `${i18n.t('forms.toast.success.segment_created')}`,
          });
        if (callback) callback(true, response, segment);
      },
      onError: (error, variables) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.segment_creation')}`,
          });
        if (callback) callback(false, error, variables);
      },
    },
  );
};

export const useDeleteSegment = ({
  setToast,
  callback,
  toggleDrawer,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (segment: SegmentType) => {
      const data = await request(
        `/contacts/segments/${segment.id}/`,
        undefined,
        'DELETE',
      );
      return data;
    },
    {
      onSuccess: (response, segment: SegmentType) => {
        queryClient.invalidateQueries('segments-count');
        queryClient.setQueryData('segments', (old: any) => {
          const newData = { ...(old || {}) };
          if (old.segment_rules && old.segment_rules.length > 0) {
            newData.segment_rules = old.segment_rules.filter(
              (o: SegmentType) => o.id !== segment.id,
            );
          }
          return newData;
        });
        if (toggleDrawer && !segment.id) toggleDrawer(false);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.segment_deleted')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.segment_deletion')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetSegmentCount = ({
  callback,
  segmentId,
  refetchOnWindowFocus,
}: MutationType & {
  segmentId: string;
}) => {
  return useQuery(
    ['segment', segmentId, 'count'],
    async () => {
      if (!segmentId) return;
      const data: CountResponseType = await request(
        `/contacts/segments/${segmentId}/contact-count`,
      );
      return data;
    },
    {
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      refetchOnWindowFocus,
      enabled: Boolean(segmentId),
      onSuccess: (response: CountResponseType) => {
        if (callback) callback(true, response);
      },
      onError: (err: any) => {
        if (callback) callback(false, err);
      },
    },
  );
};

export const useGetSegmentsCount = ({
  callback,
  enabled = true,
}: MutationType) => {
  return useQuery(
    ['segments-count'],
    async () => {
      const countResponse: CountResponseType = await request(
        '/contacts/segments/count',
      );
      return countResponse;
    },
    {
      onSuccess: (response: CountResponseType) => {
        if (callback) callback(true, response);
      },
      onError: (err: any) => {
        if (callback) callback(false, err);
      },
      enabled,
    },
  );
};
